import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import type { MonthDetailResponse } from '@/services/bankRecon'
import {
  ActiveFilterPillBar,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  loadColumnVisibility,
  removeFilterPill,
  useColumnWidths,
  type FilterGroup,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import { formatCalendarDate } from '@/utils/dateFormat'
import { filterRowsByText, filterRowsWithTree, toggleSingleColumnSort } from '../fixed-assets/fixedAssetsTableFilters'
import { FixedAssetsSortableHeader } from '../fixed-assets/FixedAssetsSortableHeader'
import { filterExceptionsTableRows, type ExceptionsTableVisibility } from './bankReconLinkerHelpers'
import { formatBankReconMoney } from './bankReconFormat'
import {
  type BankReconRow,
  bucketLabel,
  flattenBuckets,
} from './bankReconRows'
import {
  BANK_RECON_TABLE_PAGE_KEY,
  COLUMN_KEYS,
  COLUMN_LABELS,
  DEFAULT_SORT,
  DEFAULT_WIDTHS,
  FIELD_DEFINITIONS,
  buildBankReconFilterFields,
  fixedColumnStyle,
  isBankReconColumnSortable,
  type BankReconColumnKey,
} from './bankReconTableConfig'

type Props = {
  detail: MonthDetailResponse
}

function SegmentedVisibilityToggle<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
  ariaLabel: string
}) {
  return (
    <div
      className="inline-flex rounded-lg bg-gray-200 p-0.5"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`whitespace-nowrap rounded-md px-2.5 py-1 text-xs font-medium transition-all ${
            value === option.value
              ? 'bg-white text-brand-700 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default function BankReconExceptionsTable({ detail }: Props) {
  const [textSearch, setTextSearch] = useState('')
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(DEFAULT_SORT)
  const [visibility, setVisibility] = useState<ExceptionsTableVisibility>('uncleared')
  const [collapsed, setCollapsed] = useState(false)
  const [columnVisibility] = useState(() =>
    loadColumnVisibility(BANK_RECON_TABLE_PAGE_KEY, FIELD_DEFINITIONS),
  )

  const filterFields = useMemo(() => buildBankReconFilterFields(), [])

  const allRows = useMemo(() => flattenBuckets(detail), [detail])
  const visibilityFiltered = useMemo(
    () => filterExceptionsTableRows(allRows, { visibility }),
    [allRows, visibility],
  )
  const treeFiltered = useMemo(
    () => filterRowsWithTree(visibilityFiltered, filterTree, filterFields),
    [visibilityFiltered, filterTree, filterFields],
  )
  const textFiltered = useMemo(
    () =>
      filterRowsByText(treeFiltered, textSearch, (row) =>
        [
          bucketLabel(row.bucket),
          row.statementDescription,
          row.qboName,
          row.qboMemo,
          row.status,
          row.note,
        ]
          .filter(Boolean)
          .join(' '),
      ),
    [treeFiltered, textSearch],
  )
  const sortedRows = useMemo(
    () => applySorts(textFiltered, sortSpecs, FIELD_DEFINITIONS),
    [textFiltered, sortSpecs],
  )

  const { getWidth, startResize } = useColumnWidths({
    pageKey: BANK_RECON_TABLE_PAGE_KEY,
    columnKeys: [...COLUMN_KEYS],
    defaults: DEFAULT_WIDTHS,
  })

  const visibleColumnKeys = useMemo(() => {
    return COLUMN_KEYS.filter((key) => {
      if (key === 'actions') return false
      const vis = columnVisibility[key]
      return vis !== false
    })
  }, [columnVisibility])

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields],
  )
  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs],
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const handleRemoveFilterPill = useCallback(
    (pillId: string) => {
      setFilterTree(removeFilterPill(filterTree, pillId))
    },
    [filterTree],
  )

  const renderCell = (columnKey: BankReconColumnKey, row: BankReconRow): ReactNode => {
    const style: CSSProperties = fixedColumnStyle(getWidth(columnKey))
    switch (columnKey) {
      case 'bucket':
        return (
          <td key={columnKey} style={style}>
            {bucketLabel(row.bucket)}
          </td>
        )
      case 'statement_date':
        return (
          <td key={columnKey} className="tabular-nums" style={style}>
            {formatCalendarDate(row.statementDate)}
          </td>
        )
      case 'statement_description':
        return (
          <td key={columnKey} style={style}>
            {row.statementDescription ?? '—'}
          </td>
        )
      case 'statement_amount':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatBankReconMoney(row.statementAmount)}
          </td>
        )
      case 'qbo_date':
        return (
          <td key={columnKey} className="tabular-nums" style={style}>
            {formatCalendarDate(row.qboDate)}
          </td>
        )
      case 'qbo_type_num': {
        const typeLabel = [row.qboType, row.qboDocNumber].filter(Boolean).join(' / ') || '—'
        return (
          <td key={columnKey} style={style}>
            {row.url && typeLabel !== '—' ? (
              <a
                href={row.url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                {typeLabel}
              </a>
            ) : (
              typeLabel
            )}
          </td>
        )
      }
      case 'qbo_name_memo':
        return (
          <td key={columnKey} style={style}>
            {[row.qboName, row.qboMemo].filter(Boolean).join(' — ') || '—'}
          </td>
        )
      case 'qbo_amount':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatBankReconMoney(row.qboAmount)}
          </td>
        )
      case 'status':
        return (
          <td key={columnKey} style={style}>
            {row.note ? (
              <span title={row.note}>{row.status ?? row.overrideKind ?? '—'}</span>
            ) : (
              row.status ?? row.overrideKind ?? '—'
            )}
          </td>
        )
      default:
        return (
          <td key={columnKey} style={style}>
            —
          </td>
        )
    }
  }

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCollapsed((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setCollapsed((prev) => !prev)
          }
        }}
        className="flex cursor-pointer select-none items-start gap-2.5 border-b border-gray-200 px-5 py-4 hover:bg-gray-50"
        aria-expanded={!collapsed}
      >
        <ChevronDownIcon
          className={`mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            collapsed ? '-rotate-90' : ''
          }`}
          aria-hidden="true"
        />
        <div>
          <h2 className="text-base font-semibold text-gray-900">Automatically matched</h2>
          <p className="mt-1 text-xs text-gray-500">
            Auto-matched rows, ignored overrides, and optional cleared matches.
          </p>
        </div>
      </div>
      {collapsed ? null : (
      <>
      <TablePageShell className="px-0 py-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
          <SearchInput
            value={textSearch}
            onChange={setTextSearch}
            placeholder="Search transactions…"
            className="max-w-md flex-1"
          />
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedVisibilityToggle
              value={visibility}
              onChange={setVisibility}
              ariaLabel="Automatically matched rows visibility"
              options={[
                { value: 'uncleared', label: 'Show uncleared' },
                { value: 'cleared', label: 'Show cleared' },
                { value: 'all', label: 'Show all' },
              ]}
            />
            <TableToolbar
              filterCount={activeFilterCount}
              sortCount={sortSpecs.length}
              showFilters={showFilters}
              showSort={showSort}
              onToggleFilters={() => setShowFilters((open) => !open)}
              onToggleSort={() => setShowSort((open) => !open)}
            />
          </div>
        </div>
        <ActiveFilterPillBar
          pills={filterPills}
          onRemovePill={handleRemoveFilterPill}
          onClearAll={() => {
            setFilterTree(null)
            setTextSearch('')
          }}
        />
        <div className="overflow-x-auto">
          <table className="table-forecast w-full">
            <thead>
              <tr>
                {visibleColumnKeys.map((columnKey) =>
                  isBankReconColumnSortable(columnKey) ? (
                    <FixedAssetsSortableHeader
                      key={columnKey}
                      label={COLUMN_LABELS[columnKey]}
                      sortKey={columnKey}
                      sortDirection={getSortDirection(columnKey)}
                      onSort={handleRequestSort}
                      columnKey={columnKey}
                      className={
                        columnKey === 'statement_amount' || columnKey === 'qbo_amount'
                          ? 'text-right'
                          : ''
                      }
                      style={fixedColumnStyle(getWidth(columnKey))}
                      onStartResize={startResize}
                    />
                  ) : (
                    <th
                      key={columnKey}
                      className="relative"
                      style={fixedColumnStyle(getWidth(columnKey))}
                    >
                      {COLUMN_LABELS[columnKey]}
                      <ColumnResizeHandle columnKey={columnKey} onStartResize={startResize} />
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumnKeys.length}
                    className="py-8 text-center text-sm text-gray-500"
                  >
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                sortedRows.map((row) => (
                  <tr key={row.rowId}>
                    {visibleColumnKeys.map((columnKey) => renderCell(columnKey, row))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </TablePageShell>
      <FilterBuilderDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        fields={filterFields}
        root={filterTree}
        onChange={setFilterTree}
        onClearAll={() => setFilterTree(null)}
      />
      <SortBuilder
        open={showSort}
        onClose={() => setShowSort(false)}
        fields={FIELD_DEFINITIONS.filter((field) => field.sortable)}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClearAll={() => setSortSpecs(DEFAULT_SORT)}
      />
      </>
      )}
    </section>
  )
}
